import type { TrafficEvent } from '@broker/contracts';
import { useEffect, useMemo, useState } from 'react';

/**
 * The picture the console exists for: who is producing, what Broker is holding, who is consuming,
 * and every message crossing between them right now.
 *
 * Producers sit on the left, consumers on the right, Broker in the middle. A message appears as a
 * dot that travels the arrow it actually took — inbound to Broker when it is published, outbound
 * to a consumer when it is delivered — and fades when its trip is over. Nothing is invented: a
 * node is drawn only once a real message has involved it.
 */

const LIFETIME_MS = 2600;
// Il disegno è largo quanto la scheda e alto quanto basta: la console non si scorre per vedere
// il traffico, è la prima cosa che si vede. L'altezza vera la fissa il CSS (.flow).
const WIDTH = 1180;
const HEIGHT = 300;
const LEFT_X = 92;
const RIGHT_X = WIDTH - 92;
const CENTER_X = WIDTH / 2;
const CENTER_Y = HEIGHT / 2;
// Broker sta al centro ed è il protagonista del disegno: la sua scatola è più grande di quelle
// delle applicazioni, e tutto quello che le arriva o parte da lei si misura da qui.
const BROKER_W = 178;
const BROKER_H = 94;
const BROKER_HALF = BROKER_W / 2;

interface Flight {
  key: string;
  from: { x: number; y: number };
  to: { x: number; y: number };
  label: string;
  tone: 'in' | 'out' | 'bad';
  bornAt: number;
}

const columnY = (index: number, total: number): number => {
  if (total <= 1) return CENTER_Y;
  const span = HEIGHT - 80;
  return 40 + (span / (total - 1)) * index;
};

export function FlowDiagram({ events }: { events: TrafficEvent[] }) {
  const [now, setNow] = useState(() => Date.now());

  // One timer for the whole diagram, running only while something is in flight.
  const recent = useMemo(() => events.filter((event) => Date.now() - Date.parse(event.occurredAt) < LIFETIME_MS), [events, now]);

  useEffect(() => {
    if (recent.length === 0) return;
    const timer = setInterval(() => setNow(Date.now()), 120);
    return () => clearInterval(timer);
  }, [recent.length]);

  const { producers, consumers } = useMemo(() => {
    const left = new Set<string>();
    const right = new Set<string>();
    for (const event of events.slice(0, 120)) {
      if (event.direction === 'inbound' && event.from) left.add(event.from);
      if (event.direction === 'outbound' && event.to) right.add(event.to);
    }
    return { producers: [...left].sort(), consumers: [...right].sort() };
  }, [events]);

  const position = (application: string, side: 'left' | 'right') => {
    const column = side === 'left' ? producers : consumers;
    const index = column.indexOf(application);
    if (index === -1) return { x: side === 'left' ? LEFT_X : RIGHT_X, y: CENTER_Y };
    return { x: side === 'left' ? LEFT_X : RIGHT_X, y: columnY(index, column.length) };
  };

  const flights: Flight[] = recent.map((event) => {
    const age = Date.now() - Date.parse(event.occurredAt);
    const failed = event.type === 'message.failed' || event.type === 'message.dead-lettered';
    if (event.direction === 'inbound' && event.from) {
      return {
        key: `${event.sequence}`,
        from: position(event.from, 'left'),
        to: { x: CENTER_X - BROKER_HALF, y: CENTER_Y },
        label: event.messageType ?? event.channel ?? '',
        tone: 'in',
        bornAt: age,
      };
    }
    return {
      key: `${event.sequence}`,
      from: { x: CENTER_X + BROKER_HALF, y: CENTER_Y },
      to: position(event.to ?? '', 'right'),
      label: event.messageType ?? event.channel ?? '',
      tone: failed ? 'bad' : 'out',
      bornAt: age,
    };
  });

  return (
    <svg
      className="flow"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Traffico in tempo reale"
    >
      <defs>
        <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" className="flow-arrow-head" />
        </marker>
      </defs>

      {producers.map((application, index) => (
        <ApplicationNode key={`p-${application}`} x={LEFT_X} y={columnY(index, producers.length)} label={application} />
      ))}
      {consumers.map((application, index) => (
        <ApplicationNode key={`c-${application}`} x={RIGHT_X} y={columnY(index, consumers.length)} label={application} />
      ))}

      {producers.map((application, index) => (
        <line
          key={`pl-${application}`}
          className="flow-line"
          x1={LEFT_X + 44}
          y1={columnY(index, producers.length)}
          x2={CENTER_X - BROKER_HALF - 2}
          y2={CENTER_Y}
          markerEnd="url(#arrow)"
        />
      ))}
      {consumers.map((application, index) => (
        <line
          key={`cl-${application}`}
          className="flow-line"
          x1={CENTER_X + BROKER_HALF + 2}
          y1={CENTER_Y}
          x2={RIGHT_X - 44}
          y2={columnY(index, consumers.length)}
          markerEnd="url(#arrow)"
        />
      ))}

      <g className="flow-broker">
        <rect x={CENTER_X - BROKER_HALF} y={CENTER_Y - BROKER_H / 2} width={BROKER_W} height={BROKER_H} rx={18} />
        <text x={CENTER_X} y={CENTER_Y + 6} textAnchor="middle">
          BROKER
        </text>
      </g>

      {flights.map((flight) => {
        const progress = Math.min(1, flight.bornAt / LIFETIME_MS);
        const x = flight.from.x + (flight.to.x - flight.from.x) * progress;
        const y = flight.from.y + (flight.to.y - flight.from.y) * progress;
        return (
          <g key={flight.key} className={`flight ${flight.tone}`} opacity={1 - progress * 0.7}>
            <circle cx={x} cy={y} r={6} />
            {progress < 0.6 ? (
              <text x={x} y={y - 12} textAnchor="middle">
                {flight.label}
              </text>
            ) : null}
          </g>
        );
      })}

      {producers.length === 0 && consumers.length === 0 ? (
        <text className="flow-idle" x={CENTER_X} y={CENTER_Y + 78} textAnchor="middle">
          Nessun messaggio ancora: il diagramma si disegna da solo al primo publish.
        </text>
      ) : null}
    </svg>
  );
}

function ApplicationNode({ x, y, label }: { x: number; y: number; label: string }) {
  return (
    <g className="flow-node">
      <rect x={x - 44} y={y - 18} width={88} height={36} rx={10} />
      <text x={x} y={y + 4} textAnchor="middle">
        {label.length > 12 ? `${label.slice(0, 11)}…` : label}
      </text>
    </g>
  );
}
